/**
 * DeleteSedDialog — SDL-3 confirmation modal for the destructive
 * `Delete SED` button on `SiteEvidenceToolbar`. Mirrors
 * `DeleteSiteDialog`'s visual shell one scope down: a SED is one rung
 * smaller than a site, so:
 *
 *   - we DO NOT require a typed-name confirm (the action is reversible
 *     via Rebuild SED; the worst case is a few minutes of waiting),
 *   - we DO surface a cascade-safety preview (how many docs stay, how
 *     many reports keep their pinned snapshot, what untouched fields
 *     remain) so the user knows exactly what survives,
 *   - we DO disable the action while a rebuild is in flight (the
 *     toolbar already gates this, but a second guard here means a
 *     race-against-rebuild can't bypass the toolbar's disabled state).
 *
 * Composes with SDL-1 (toolbar button) and SDL-2 (store action). The
 * host owns the busy flag — the dialog reports busy through its own
 * Cancel button so the parent can dismiss after the action lands.
 */
import React, { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '../ui/button';
import type { SiteEntity } from '../../types/site';

interface Props {
  site: SiteEntity;
  open: boolean;
  /** Count of reports pinned to the site (any report still in the
   *  store referencing this site). Reports keep their gen-time SED
   *  snapshot regardless of whether the live SED gets deleted, so we
   *  surface this number as reassurance — NOT as a block. */
  reportsPinnedCount: number;
  /** Count of user-uploaded documents on the site. Documents are NOT
   *  touched by Delete SED — surfaced for the same reason. */
  docCount: number;
  /** Disables the Confirm action when a rebuild is in flight. The
   *  toolbar already hides the button in this state; this is a
   *  secondary guard against a slow user who opened the modal before
   *  a rebuild kicked off. */
  rebuildActive: boolean;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

export const DeleteSedDialog: React.FC<Props> = ({
  site,
  open,
  reportsPinnedCount,
  docCount,
  rebuildActive,
  onConfirm,
  onCancel,
}) => {
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const handleConfirm = async () => {
    if (busy || rebuildActive) return;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      // Parent closes the modal; reset busy in case the parent keeps
      // the dialog open after a failure so the user can retry.
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60"
      onClick={busy ? undefined : onCancel}
      data-testid="delete-sed-dialog"
    >
      <div
        className="bg-background w-[520px] max-h-[90vh] overflow-y-auto shadow-2xl border border-border"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — same alert-warning shell as DeleteSiteDialog. */}
        <div className="px-5 py-4 border-b border-border flex items-start gap-3">
          <div className="w-9 h-9 border border-danger/40 bg-danger/15 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4 text-danger" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold uppercase tracking-widest text-text-primary truncate">
              Delete Site Evidence Document?
            </h2>
            <p className="text-[10px] text-text-secondary mt-0.5 truncate">
              {site.name}
              {site.sedId && (
                <>
                  {' · '}
                  <span className="font-mono">{site.sedId.slice(0, 12)}</span>
                </>
              )}
            </p>
          </div>
          <button
            onClick={onCancel}
            disabled={busy}
            className="p-1 text-text-muted hover:text-text-primary disabled:opacity-30"
            aria-label="Close dialog"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Cascade-safety body — what survives + what goes. */}
        <div className="px-5 py-4 space-y-3 text-[11px] text-text-primary">
          <p>
            This clears the SED state for{' '}
            <b className="text-text-primary">{site.name}</b>. The following
            remain <b className="text-success">UNTOUCHED</b>:
          </p>
          <ul className="list-disc list-inside text-[11px] text-text-secondary space-y-0.5 ml-1">
            <li data-testid="delete-sed-dialog-docs">
              <span className="font-mono text-text-primary">{docCount}</span>{' '}
              uploaded document{docCount === 1 ? '' : 's'}
            </li>
            <li data-testid="delete-sed-dialog-reports">
              <span className="font-mono text-text-primary">{reportsPinnedCount}</span>{' '}
              existing report{reportsPinnedCount === 1 ? '' : 's'} — each keeps
              the SED snapshot it was generated against
            </li>
            <li>Project intent, site context, boundary, location</li>
          </ul>
          <p className="text-[11px] text-text-muted">
            After delete you can re-bootstrap by clicking{' '}
            <b className="text-text-primary">Rebuild SED</b> — providers will
            be re-queried (~2–5 min).
          </p>
          {rebuildActive && (
            <div
              className="p-2.5 border border-warning/40 bg-warning/15 text-[10px] text-warning"
              data-testid="delete-sed-dialog-rebuild-warning"
            >
              A rebuild is in flight. Wait for it to finish before deleting,
              or this action will be blocked.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 type-body uppercase tracking-widest"
            onClick={onCancel}
            disabled={busy}
            autoFocus
            data-testid="delete-sed-dialog-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="mission-danger"
            size="sm"
            className="h-8 type-body uppercase tracking-widest"
            disabled={busy || rebuildActive}
            onClick={handleConfirm}
            data-testid="delete-sed-dialog-confirm"
          >
            {busy ? 'Deleting…' : 'Delete SED'}
          </Button>
        </div>
      </div>
    </div>
  );
};
